import { MessageCircle, Shield, X } from 'lucide-react';
import { useEffect } from 'react';
import type { Profile } from '../types';
import { relativeLastSeen } from '../lib/format';
import Avatar from './Avatar';

export default function UserProfileModal({
  profile,
  online,
  onClose,
  onMessage,
}: {
  profile: Profile;
  online: boolean;
  onClose: () => void;
  onMessage?: (profile: Profile) => void;
}) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 py-5 backdrop-blur-md">
      <section className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-ink-850 shadow-panel">
        <div className="h-28 bg-[radial-gradient(circle_at_25%_25%,rgba(34,211,238,.36),transparent_30%),linear-gradient(135deg,rgba(79,140,255,.72),rgba(139,92,246,.38),rgba(5,7,13,.96))]" />
        <div className="-mt-10 px-5 pb-5">
          <div className="flex items-start justify-between gap-4">
            <Avatar name={profile.display_name} src={profile.avatar_url} online={online} size="lg" />
            <button
              type="button"
              onClick={onClose}
              className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-black/25 text-slate-400 transition hover:bg-white/10 hover:text-white"
              aria-label="Close profile"
            >
              <X size={18} />
            </button>
          </div>

          <div className="mt-4">
            <h2 className="truncate text-xl font-black text-white">{profile.display_name}</h2>
            <p className="truncate text-sm text-slate-500">@{profile.username}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-lg bg-relay-green/10 px-2.5 py-1 text-xs font-semibold text-relay-green">
                {online ? 'Online' : `Seen ${relativeLastSeen(profile.last_seen)}`}
              </span>
              <span className="inline-flex items-center gap-1 rounded-lg bg-relay-cyan/10 px-2.5 py-1 text-xs font-semibold text-relay-cyan">
                <Shield size={12} />
                {profile.role}
              </span>
            </div>
          </div>

          <section className="mt-5 rounded-xl border border-white/10 bg-white/[0.035] p-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Description</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-200">
              {profile.bio || 'No profile description yet.'}
            </p>
          </section>

          {onMessage && (
            <button
              type="button"
              onClick={() => onMessage(profile)}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-relay-blue to-relay-violet px-4 py-3 text-sm font-bold text-white shadow-glow transition hover:brightness-110"
            >
              <MessageCircle size={17} />
              Message
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
